import { useEffect, useMemo, useRef, useState, startTransition } from 'react';
import { Layout, Nav, Modal } from '@douyinfe/semi-ui';
import { usePrefixCls } from 'src/hook/useConfig';
import { useLocale } from '../../../../locales';
import { MenuItem } from './menuList';
import { useLocation, useNavigate } from 'react-router-dom';
import useStore from 'src/store';
import { findMenuByPath } from '../../../../utils/utils';
import useCache from '../../../../hook/useCache';
import { CacheEnum } from '../../../../enum/cache';
import logo from '@/assets/image/logo.svg';
import { convertBackendMenuToSiderMenu } from '@/utils/menuConverter';
import usePermission from '../../../../hooks/usePermission';
import UnauthorizedPage from '../../../../components/unauthorized';
import useFinanceStore from '@/views/finance/store';
import './styles/index.scss';

const { Sider } = Layout;

const HIDDEN_SIDER_PATHS = new Set<string>(['/extension']);

function filterSiderMenuItems(list: MenuItem[]): MenuItem[] {
  return list
    .filter((menu) => !menu.path || !HIDDEN_SIDER_PATHS.has(menu.path))
    .map((menu) => ({
      ...menu,
      items: menu.items ? filterSiderMenuItems(menu.items) : menu.items,
    }));
}

const SiderIndex = () => {
  const prefixCls = usePrefixCls('layout-sider');
  const { setCache } = useCache();
  const { pathname } = useLocation();
  const { tabList, setTabList, siderCollapsed, setSiderCollapsed } = useStore();
  const setOpenRouter = useStore((state) => state.setOpenRouter);
  const localeMode = useStore((state) => state.localeMode);
  const navigate = useNavigate();
  const { formatMessage } = useLocale(); // 确保 useLocale 已经导入并获取 formatMessage
  const { checkAndNavigate } = usePermission();
  const [showUnauthorizedModal, setShowUnauthorizedModal] = useState(false);
  const getMenu = useMemo(() => {
    const menu = convertBackendMenuToSiderMenu();
    const visibleMenu = filterSiderMenuItems(menu);
    return setMenuText(visibleMenu);
  }, [localeMode]);
  const menuTree = useRef<MenuItem[]>([]);
  useEffect(() => {
    menuTree.current = findMenuByPath({
      path: pathname,
      menus: convertBackendMenuToSiderMenu(),
    });
    // 设置面包屑数据
    setOpenRouter(menuTree.current);
  }, [pathname]);
  // 菜单名国际化
  function setMenuText(list: MenuItem[]): MenuItem[] {
    return list.map((menu: MenuItem) => {
      // 确保 menu.text 是字符串类型用于国际化
      const textKey =
        typeof menu.text === 'string' ? menu.text : menu.textId || '';
      const displayText = formatMessage({ id: textKey });

      return {
        ...menu,
        textId: textKey,
        text: displayText,
        items: menu.items ? setMenuText(menu.items) : [],
      };
    });
  }
  // 设置选中菜单 根据路由变化
  const getMenuSelect = useMemo(() => {
    const menu = findMenuByPath({
      path: pathname,
      menus: convertBackendMenuToSiderMenu(),
    });
    return menu.map((item) => {
      return item.itemKey;
    });
  }, [pathname]);
  // 设置面包屑和标签页
  function setMenuHandle(data: any) {
    const { path, text, textId } = data;

    if (!path) return;
    const hasTab = tabList.findIndex((item) => {
      return item.itemKey === path;
    });
    if (hasTab < 0) {
      startTransition(() => {
        setTabList([
          ...tabList,
          {
            itemKey: path,
            tab: textId || text || '未知',
            closable: true,
          },
        ]);
      });
    }
  }
  // 点击菜单
  function selectMenu(data: any) {
    const path = data.selectedItems[0].path;
    const menuItem = data.selectedItems[0];
    setMenuHandle(menuItem);
    if (path === '/finance') {
      useFinanceStore.getState().reset();
    }
    startTransition(() => {
      navigate(path);
    });
  }
  // 设置下拉展开
  const onOpenChange = (data: any) => {
    setCache({
      key: CacheEnum.openMenu,
      value: [...data.openKeys],
      storage: sessionStorage,
    });
  };
  return (
    <>
      <Sider className={prefixCls}>
        <Nav
          defaultOpenKeys={getMenuSelect}
          style={{ height: '100%' }}
          bodyStyle={{ height: 'calc(100vh - 168px)' }}
          onOpenChange={onOpenChange}
          selectedKeys={getMenuSelect}
          onSelect={selectMenu}
          onClick={(data) => {
            // 查找对应的菜单项
            const allMenus = convertBackendMenuToSiderMenu();
            let targetMenuItem: any = null;

            // 在所有菜单项中查找匹配的项
            for (const menu of allMenus) {
              if (menu.itemKey === data.itemKey) {
                targetMenuItem = menu;
                break;
              }
              // 如果有子菜单，也要查找
              if (menu.items) {
                for (const subMenu of menu.items) {
                  if (subMenu.itemKey === data.itemKey) {
                    targetMenuItem = subMenu;
                    break;
                  }
                }
                if (targetMenuItem) break;
              }
            }

            // 如果找到菜单项且有路径，则跳转
            if (
              targetMenuItem &&
              targetMenuItem.path &&
              targetMenuItem.path !== pathname
            ) {
              setMenuHandle(targetMenuItem);
              startTransition(() => {
                navigate(targetMenuItem.path);
              });
            }
          }}
          isCollapsed={siderCollapsed}
          onCollapseChange={setSiderCollapsed}
          header={{
            logo: <img src={logo} alt='logo' />,
            text: formatMessage({ id: 'web.app.title' }), // 修改此处
          }}
          limitIndent={false}
          items={getMenu}
          footer={{
            collapseButton: true,
          }}
        ></Nav>
      </Sider>

      {/* 权限不足提示弹窗 */}
      <Modal
        title='访问权限'
        visible={showUnauthorizedModal}
        onCancel={() => setShowUnauthorizedModal(false)}
        footer={null}
        width={500}
        className='unauthorized-modal'
      >
        <UnauthorizedPage
          onAuthorize={() => {
            setShowUnauthorizedModal(false);
          }}
        />
      </Modal>
    </>
  );
};

export default SiderIndex;
